/**
 * CancelEventDialog — iter-92
 *
 * Distinct from "Delete" — cancellation preserves the event row (and all
 * its attendance history / attachments / repertoire) but flips status to
 * "cancelled" and optionally emails everyone an .ics CANCEL so their
 * calendar entries auto-clear.
 */
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { XCircle, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../App";

const AUDIENCE_OPTIONS = [
  { value: "all", label: "Everyone", hint: "Members + invited deps" },
  { value: "attending_maybe", label: "Attending + Maybe only" },
  { value: "attending", label: "Attending only" },
];

export default function CancelEventDialog({ open, onOpenChange, eventId, eventTitle, onCancelled }) {
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true);
  const [audience, setAudience] = useState("all");
  const [sending, setSending] = useState(false);

  const handleCancel = async () => {
    if (!reason.trim()) {
      toast.error("Please provide a reason — members will see this in the email.");
      return;
    }
    setSending(true);
    try {
      const res = await apiClient.post(`/events/${eventId}/cancel`, {
        reason: reason.trim(),
        notify,
        audience,
      });
      const notified = res.data?.notified ?? 0;
      const total = res.data?.total_recipients ?? 0;
      if (notify && total > 0) {
        toast.success(`Event cancelled. Notified ${notified} of ${total}.`);
      } else {
        toast.success("Event cancelled.");
      }
      onCancelled?.();
      onOpenChange(false);
      setReason("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel event");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cancel-event-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-400">
            <XCircle className="w-5 h-5" />
            Cancel event
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 flex gap-2 text-sm">
            <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="text-red-100">
              You're about to cancel <strong>{eventTitle || "this event"}</strong>. The event will be
              kept (attendance history preserved) but marked Cancelled. RSVPs will be locked.
            </div>
          </div>

          <div>
            <Label htmlFor="cancel-reason" className="text-xs uppercase tracking-wide text-muted-foreground">
              Reason <span className="text-red-400">*</span>
            </Label>
            <Textarea
              id="cancel-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Venue double-booked — we'll reschedule for next month."
              rows={3}
              className="mt-2"
              data-testid="cancel-reason-input"
            />
          </div>

          <label className="flex items-start gap-3 rounded-md border border-border/60 p-3 cursor-pointer hover:bg-muted/40">
            <Checkbox
              checked={notify}
              onCheckedChange={(v) => setNotify(!!v)}
              data-testid="cancel-notify-checkbox"
            />
            <div>
              <div className="font-medium text-sm">Email everyone & remove from their calendars</div>
              <div className="text-xs text-muted-foreground">
                Sends a cancellation email with a calendar update that auto-removes the entry.
              </div>
            </div>
          </label>

          {notify && (
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Who to notify</Label>
              <RadioGroup value={audience} onValueChange={setAudience} className="mt-2 space-y-2">
                {AUDIENCE_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    htmlFor={`cancel-audience-${opt.value}`}
                    className="flex items-start gap-3 rounded-md border border-border/60 p-2.5 cursor-pointer hover:bg-muted/40"
                    data-testid={`cancel-audience-${opt.value}`}
                  >
                    <RadioGroupItem value={opt.value} id={`cancel-audience-${opt.value}`} className="mt-0.5" />
                    <div>
                      <div className="font-medium text-sm">{opt.label}</div>
                      {opt.hint && <div className="text-xs text-muted-foreground">{opt.hint}</div>}
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending} data-testid="cancel-dialog-back-btn">
            Back
          </Button>
          <Button
            onClick={handleCancel}
            disabled={sending || !reason.trim()}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="cancel-dialog-confirm-btn"
          >
            {sending ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cancelling…</>
            ) : (
              <><XCircle className="w-4 h-4 mr-2" /> Cancel event</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
